import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, IconButton, Grid, useTheme, useMediaQuery, Menu, MenuItem, Container } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import StoreContext from '../../../../react-core-lib/components/app/store-context';
import { useNavigate } from 'react-router-dom';
import UserApi from '../../../../rest-api/user-api';
import { useSnackbar } from '../../SnackbarProvider';
import PatientRow from '../../../UIControls/PatientRow';
import AddPatientDialog from './add-patient-modal';
import { FeedbackCTA } from '../../feedback-cta';
import WhiteboardCard from './WhiteboardCard';
import GradientBanner from '../../../UIControls/GradientBanner';
import { archiveWhiteboard } from '../../../helper/whiteboardUtils';
import PinkGradientButtonWithArrow from '../../../UIControls/PinkGradientButtonWithArrow';

const MyClinicWhiteboards = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { patientId } = useParams();
    const { userDataStore, whiteboardStore, authStore, imageStore } = useContext(StoreContext);
    const [whiteboards, setWhiteboards] = useState([]);
    const [filteredWhiteboards, setFilteredWhiteboards] = useState([]);
    const [page, setPage] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [anchorEl, setAnchorEl] = useState(null);
    const [sortBy, setSortBy] = useState('RECENT');
    const [imageMap, setImageMap] = useState({});
    const [sortDirection, setSortDirection] = useState('desc'); // 'desc' = Newest first
    const [hasSortedRecently, setHasSortedRecently] = useState(false);
    const [favourites, setFavourites] = useState(userDataStore.userData.whiteboardsFavourites || []);

    const [patient, setPatient] = useState(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingPatient, setEditingPatient] = useState(null);

    const whiteboardsPerPage = 6;
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
    const showSnackbar = useSnackbar();

    const paginatedWhiteboards = filteredWhiteboards.slice(page * whiteboardsPerPage, (page + 1) * whiteboardsPerPage);

    useEffect(() => {
        const stored = sessionStorage.getItem('myClinicSelectedPatient');
        if (stored) {
            setPatient(JSON.parse(stored));
            sessionStorage.removeItem('myClinicSelectedPatient');
        }
    }, []);

    useEffect(() => {
        if (!whiteboardStore.isInitialized) return;

        let whiteboardsData = Object.values(whiteboardStore.whiteboardData);

        // console.log('Updating Clinic Whiteboards State');

        // All whiteboards for this patient, excluding archived
        whiteboardsData = whiteboardsData
            .filter((wb) => wb.whiteboardStatus !== 'archived' && wb.patientId === patientId)
            .sort((a, b) => b.lastUpdated - a.lastUpdated);

        setWhiteboards(whiteboardsData);
        setFilteredWhiteboards(whiteboardsData);
    }, [whiteboardStore.whiteboardData, whiteboardStore.isInitialized, patientId]);

    useEffect(() => {
        // Fetch image previews
        const fetchImages = async () => {
            const images = {};
            for (const wb of filteredWhiteboards) {
                if (wb.canvasImageLink) {
                    const image = await imageStore.getImageFromIndexedDB(wb.canvasImageLink);
                    if (image?.data) images[wb.canvasImageLink] = image.data;
                }
            }
            setImageMap(images);
        };
        fetchImages();
    }, [filteredWhiteboards]);

    const handleSearchInputChange = (e) => {
        const value = e.target.value.toLowerCase();
        setSearchTerm(value);
        const all = Object.values(whiteboardStore.whiteboardData).filter((wb) => wb.patientId === patientId);
        const filtered = all.filter(
            (wb) =>
                wb.handoutContentName?.toLowerCase().includes(value) ||
                wb.handoutAlphabeticalName?.toLowerCase().includes(value) ||
                wb.handoutDocumentTitle?.toLowerCase().includes(value) ||
                wb.notes?.toLowerCase().includes(value) ||
                wb.fileTitle?.toLowerCase().includes(value) ||
                wb.patientName?.toLowerCase().includes(value),
        );
        setFilteredWhiteboards(filtered);
        setPage(0);
    };

    const handleNextPage = () => {
        if ((page + 1) * whiteboardsPerPage < filteredWhiteboards.length) {
            setPage(page + 1);
        }
    };

    const handlePrevPage = () => {
        if (page > 0) {
            setPage(page - 1);
        }
    };

    const handleOpenMenu = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = (option) => {
        const favouriteIds = userDataStore.userData.whiteboardsFavourites || [];

        if (option === 'RECENT') {
            const newDirection = sortDirection === 'desc' ? 'asc' : 'desc';
            setSortDirection(newDirection);
            setHasSortedRecently(true);
            setSortBy('RECENT');

            const sorted = [...whiteboards].sort((a, b) =>
                newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
            );
            setFilteredWhiteboards(sorted);
            setPage(0);
        }

        if (option === 'FAVOURITES') {
            const isSameSort = sortBy === 'FAVOURITES';
            const newDirection = isSameSort && sortDirection === 'desc' ? 'asc' : 'desc';

            setSortDirection(newDirection);
            setHasSortedRecently(false);
            setSortBy('FAVOURITES');

            const favouritesGroup = whiteboards
                .filter((wb) => favouriteIds.includes(wb.whiteboardId))
                .sort((a, b) =>
                    newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
                );

            const nonFavouritesGroup = whiteboards
                .filter((wb) => !favouriteIds.includes(wb.whiteboardId))
                .sort((a, b) =>
                    newDirection === 'asc' ? a.lastUpdated - b.lastUpdated : b.lastUpdated - a.lastUpdated,
                );

            const sorted = [...favouritesGroup, ...nonFavouritesGroup];
            setFilteredWhiteboards(sorted);
            setPage(0);
        }

        setAnchorEl(null);
    };

    const handleArchiveWhiteboard = (wb) =>
        archiveWhiteboard(
            wb,
            userDataStore,
            whiteboardStore,
            authStore,
            setWhiteboards,
            setFilteredWhiteboards,
            showSnackbar,
        );

    const handleToggleFavourite = async (whiteboardId) => {
        const isMultiUser = userDataStore?.subscriptionData?.accountType?.includes('multiuser');
        const isFavourite = userDataStore.userData.whiteboardsFavourites?.includes(whiteboardId);
        const updatedFavourites = isFavourite
            ? userDataStore.userData.whiteboardsFavourites.filter((id) => id !== whiteboardId)
            : [...(userDataStore.userData.whiteboardsFavourites || []), whiteboardId];

        if (!isFavourite && userDataStore.userData.whiteboardsFavourites.length >= 30) {
            showSnackbar('You can only mark up to 30 whiteboards as favourites.', 'warning');
            return;
        }

        // Update local store
        userDataStore.setUserData({
            ...userDataStore.userData,
            whiteboardsFavourites: updatedFavourites,
        });

        // Send update to backend
        const payload = {
            whiteboardsFavourites: updatedFavourites,
        };

        setFavourites(updatedFavourites);

        try {
            if (isMultiUser) {
                payload.action = 'updateMultiuser';
                payload.emailAddress = userDataStore.userData.emailAddress;
                payload.inviteId = userDataStore.userData.inviteId;
                await UserApi.updateClinicianDataMultiuser(authStore.idToken, payload);
            } else {
                await UserApi.updateClinicianData(authStore.idToken, payload);
            }
        } catch (err) {
            console.error('Failed to update favourites', err);
        }
    };

    const handleEditWhiteboard = (whiteboard) => {
        if (!whiteboard.handoutId) {
            console.error('Missing handoutContentId for whiteboard:', whiteboard);
            return;
        }

        // Set whiteboard state in localStorage so it's loaded on Handout page
        localStorage.setItem(
            'lastWhiteboardState',
            JSON.stringify({
                whiteboardId: whiteboard.whiteboardId,
                handoutId: whiteboard.handoutId,
                canvasImageLink: whiteboard.canvasImageLink,
                canvasJsonLink: whiteboard.canvasJsonLink,
                fileName: whiteboard.fileName || '',
                eTag: whiteboard.eTag,
                ...(whiteboard.lastUsedColours && { lastUsedColours: whiteboard.lastUsedColours }),
                ...(whiteboard.updatedByFirstName && {
                    updatedByFirstName: whiteboard.updatedByFirstName,
                }),
                ...(whiteboard.updatedByLastName && {
                    updatedByLastName: whiteboard.updatedByLastName,
                }),
                ...(whiteboard.diagramVariant && { diagramVariant: whiteboard.diagramVariant }),
            }),
        );

        sessionStorage.setItem('autoloadWhiteboardFromLibrary', 'true');
        // pass details for disclaimer
        sessionStorage.setItem(
            'whiteboardVetName',
            `${whiteboard.updatedByFirstName || ''} ${whiteboard.updatedByLastName || ''}`.trim(),
        );
        sessionStorage.setItem('whiteboardClinicName', userDataStore?.userData?.clinicName || '');
        // Navigate to the handout page
        navigate(`/handout/${whiteboard.handoutId}`);
    };

    const handleEditPatient = (p) => {
        setEditingPatient(p);
        setEditDialogOpen(true);
    };

    const handleArchive = async (patientId) => {
        try {
            const updatedPatient = {
                ...patient,
                patientStatus: 'archived',
            };

            await UserApi.createOrUpdatePatient(authStore.idToken, updatedPatient);

            setPatient(updatedPatient);

            showSnackbar('Patient archived');
        } catch (err) {
            showSnackbar('Failed to archive patient', 'error');
        }
    };

    const columns = isMobileOrTablet
        ? []
        : [
              { key: 'patientName', label: 'Name' },
              { key: 'clientSurname', label: 'Surname' },
              { key: 'DOB', label: 'DOB' },
              { key: 'microchipNumber', label: 'Microchip' },
              { key: 'species', label: 'Species' },
              { key: 'breed', label: 'Breed' },
              { key: 'clinicPatientId', label: 'PMS ID' },
              { key: 'patientStatus', label: 'Status' },
              { key: 'actions', label: 'Operation' },
          ];

    return (
        <>
            {/* Gradient Header */}
            <GradientBanner
                title="My Clinic"
                subtitle="Overview of patient's whiteboards"
                placeholder="Search whiteboards..."
                searchTerm={searchTerm}
                onSearchChange={handleSearchInputChange}
                isMobile={isMobile}
                isTablet={isMobileOrTablet}
                height="auto"
                paddingY={isMobile ? '48px' : '64px'}
                paddingBottom={isMobile ? '40px' : '64px'}
                className="search-banner"
            />

            <Container
                component={'section'}
                maxWidth={'xl'}
                sx={{ backgroundColor: '#F0F0FC', px: isMobileOrTablet ? 2 : 6, pt: 3 }}
                // className="clinic-table"
            >
                <Box sx={{ width: '100%', margin: '0 auto', mt: 4, position: 'relative' }}>
                    {patient && (
                        <Box
                            component="section"
                            mx={3}
                            sx={{ backgroundColor: 'white', borderRadius: 2, overflowX: 'auto' }}
                        >
                            {!isMobileOrTablet && (
                                <Grid container sx={{ height: '60px', px: 2, minWidth: 1100 }}>
                                    {columns.map((col) => (
                                        <Grid
                                            item
                                            key={col.key}
                                            xs={col.key === 'microchipNumber' ? 2 : col.key === 'breed' ? 2 : 1}
                                            sx={{ display: 'flex', alignItems: 'center' }}
                                        >
                                            <Typography>{col.label}</Typography>
                                        </Grid>
                                    ))}
                                </Grid>
                            )}

                            <PatientRow
                                key={patient.id}
                                patient={patient}
                                onEdit={handleEditPatient}
                                onArchive={handleArchive}
                                hasWhiteboard={true}
                                onView={() => {}}
                                hideWhiteboardColumn={true}
                                customColumnWidths={{
                                    actions: 2,
                                }}
                                compactStatus={true}
                                isMobileOrTablet={isMobileOrTablet}
                            />
                        </Box>
                    )}
                    {/* Back Button and Sort By - Responsive */}
                    <Box
                        sx={{
                            my: 4,
                            px: 3,
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'stretch', sm: 'center' },
                                gap: 2,
                            }}
                        >
                            <PinkGradientButtonWithArrow
                                label="Back to My Patients"
                                onClick={() => {
                                    navigate('/my-clinic');
                                }}
                                arrowPosition="left"
                            />

                            {/* Sort by */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: { xs: 'flex-end', sm: 'flex-start' },
                                    m: 0,
                                }}
                            >
                                {/* Sort By Text */}
                                <Typography
                                    onClick={handleOpenMenu}
                                    sx={{
                                        fontFamily: 'Inter',
                                        fontWeight: 600,
                                        fontSize: '14px',
                                        lineHeight: '14px',
                                        letterSpacing: '2px',
                                        textTransform: 'uppercase',
                                        color: '#1919194D',
                                        cursor: 'pointer',
                                    }}
                                >
                                    SORT BY{' '}
                                    <Typography
                                        component="span"
                                        sx={{
                                            color: '#000453',
                                        }}
                                    >
                                        {sortBy}
                                    </Typography>
                                </Typography>

                                {/* Dropdown Icon */}
                                <IconButton onClick={handleOpenMenu} sx={{ ml: 1, p: 0 }}>
                                    {sortBy === 'RECENT' && hasSortedRecently ? (
                                        sortDirection === 'desc' ? (
                                            <ExpandMoreIcon sx={{ color: '#000453' }} />
                                        ) : (
                                            <ExpandLessIcon sx={{ color: '#000453' }} />
                                        )
                                    ) : (
                                        <ExpandMoreIcon sx={{ color: '#000453' }} />
                                    )}
                                </IconButton>

                                {/* Dropdown Menu */}
                                <Menu
                                    anchorEl={anchorEl}
                                    open={Boolean(anchorEl)}
                                    onClose={() => handleCloseMenu(null)}
                                >
                                    <MenuItem onClick={() => handleCloseMenu('RECENT')} sx={{ color: '#000453' }}>
                                        RECENT
                                    </MenuItem>
                                    <MenuItem onClick={() => handleCloseMenu('FAVOURITES')} sx={{ color: '#000453' }}>
                                        FAVOURITES
                                    </MenuItem>
                                </Menu>
                            </Box>
                        </Box>
                    </Box>
                    {filteredWhiteboards.length === 0 ? (
                        <Box sx={{ mt: 6, textAlign: 'center' }}>
                            <Typography variant="h6" sx={{ color: '#000453' }}>
                                No whiteboards available yet.
                            </Typography>

                            <Typography variant="body1" sx={{ mt: 1, color: '#000453' }}>
                                To start a whiteboard session, select your handout of choice and then toggle on
                                whiteboard.
                            </Typography>
                            <Typography variant="body1" sx={{ color: '#000453' }}>
                                Annotate our illustration and then click save. Once saved your whiteboard will appear
                                here.
                            </Typography>
                            {/* Button with spacing */}
                            <Box
                                sx={{
                                    mt: 3,
                                    mb: 3,
                                    display: 'flex',
                                    justifyContent: { xs: 'center', sm: 'flex-end' },
                                }}
                            >
                                <PinkGradientButtonWithArrow
                                    label="Search Handouts"
                                    onClick={() => navigate('/search')}
                                />
                            </Box>
                        </Box>
                    ) : (
                        // Whiteboards
                        <Grid
                            container
                            spacing={3}
                            px={isMobile ? '48px' : '66px'}
                            sx={{ position: 'relative', ml: '0px!important', width: '100%' }}
                        >
                            {paginatedWhiteboards.map((whiteboard, index) => (
                                <WhiteboardCard
                                    key={whiteboard.whiteboardId || index}
                                    whiteboard={whiteboard}
                                    isMobile={isMobile}
                                    userDataStore={userDataStore}
                                    authStore={authStore}
                                    whiteboardStore={whiteboardStore}
                                    showSnackbar={showSnackbar}
                                    handleToggleFavourite={handleToggleFavourite}
                                    handleEditWhiteboard={handleEditWhiteboard}
                                    handleArchiveWhiteboard={handleArchiveWhiteboard}
                                    imageMap={imageMap}
                                    index={index}
                                />
                            ))}

                            {/* Pagination Controls */}
                            {filteredWhiteboards.length > 6 && (
                                <>
                                    <IconButton
                                        onClick={handlePrevPage}
                                        disabled={page === 0}
                                        sx={{
                                            position: 'absolute',
                                            left: '-10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 10,
                                            backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            '&:hover': { backgroundColor: 'rgba(0, 4, 83, 0.12)' },
                                        }}
                                    >
                                        <ChevronLeftRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                    </IconButton>

                                    <IconButton
                                        onClick={handleNextPage}
                                        disabled={(page + 1) * whiteboardsPerPage >= filteredWhiteboards.length}
                                        sx={{
                                            position: 'absolute',
                                            right: '-10px',
                                            top: '50%',
                                            transform: 'translateY(-50%)',
                                            zIndex: 10,
                                            backgroundColor: 'rgba(0, 4, 83, 0.08)',
                                            width: '50px',
                                            height: '50px',
                                            borderRadius: '50%',
                                            '&:hover': { backgroundColor: 'rgba(0, 4, 83, 0.12)' },
                                        }}
                                    >
                                        <ChevronRightRoundedIcon sx={{ fontSize: '32px', color: '#000453' }} />
                                    </IconButton>
                                </>
                            )}
                        </Grid>
                    )}

                    {editingPatient && (
                        <AddPatientDialog
                            open={editDialogOpen}
                            onClose={() => setEditDialogOpen(false)}
                            showSnackbar={showSnackbar}
                            onSubmit={async (updatedData) => {
                                try {
                                    const updatedPatient = {
                                        ...editingPatient,
                                        ...updatedData,
                                        patientId: editingPatient.patientId,
                                    };

                                    await UserApi.createOrUpdatePatient(authStore.idToken, updatedPatient);

                                    setPatient(updatedPatient);

                                    showSnackbar('Patient updated!', 'success');
                                } catch (error) {
                                    console.error('Failed to update patient:', error);
                                    showSnackbar('Failed to update patient.', 'error');
                                } finally {
                                    setEditDialogOpen(false);
                                }
                            }}
                            initialValues={editingPatient}
                        />
                    )}
                </Box>
                {/* New Feedback Section */}
                <FeedbackCTA />
            </Container>
        </>
    );
};

export default MyClinicWhiteboards;
